import { ListContextProvider, useGetMainList, useListContext, useListController, useRecordContext, ReduxState } from "ra-core"
import { Box, Card, CardHeader, Divider, Autocomplete, TextField as MuiTextField, Backdrop, Typography, Tab, Tabs, Button, 
  CircularProgress, FormHelperText, Grid, Stack, FormControlLabel, FormGroup, Switch} from "@mui/material"
  import { TabContext, TabPanel } from "@mui/lab";
  import { useFormik, useFormikContext } from "formik";
  import { FC, useEffect, useMemo, useRef, useState } from "react";
  import { Content } from "~/components/content/Content"
  import { OdResourcePageProps } from "~/types/resource"
import { Datagrid } from "~/components/list/datagrid"
import Scrollbar from "~/components/Scrollbar"
import { RecPatch } from "~/dataProvider/tools"
import { FunctionField } from "~/components/field"
import { EmailType, NotificationAssignDto, RoleResponseDto, UserResponseDto } from "~/gen/api/user"
import { pages, sorts } from "~/utils/paramsWithGetMainList"
import OdForm from "~/components/formik/OdForm";
import { cloneDeep, debounce } from "lodash"
import { useResourceRegister } from "~/utils/resourceRegister"
import CreateOrEdit from "~/controller/CreateOrEdit"
import { useSimpleSaveAction } from "~/controller/SaveContextProvider"
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers"
import { useHasPermit } from "~/authProvider/useHasPermit";
import { ORGANIZATION_USERS } from "~/constants";
import WithSave from "~/controller/WithSave";
import { ExcessiveDecryptSettingDto, ExcessiveDecryptSettingInput } from "~/gen/api/securefile";
import Edit from "~/controller/Edit";
import { useHasFeature } from "~/utils/useHasFeature";
import { useLocation, useHistory } from "react-router";
import { useSelector } from 'react-redux';


const excessiveDecryptResource = 'settings-clients-securefile-excessive-decrypt-config'

type TabOption = 'general' | 'excessive-decrypt'

const NotificationSettings: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: 'org-roles' })  
  useResourceRegister({ name: 'settings-clients-securefile-excessive-decrypt-config' })

  const hasSecureFileFeature = useHasFeature()?.OnDataSecureFile ?? false

  const { resource } = props
  const location = useLocation()
  const history = useHistory()
  const hash = location.hash
  const [states, setStates] = useState({
    currTab: !hasSecureFileFeature? 'general' : hash ? hash.slice(1) : 'general'
  })
  const isRegistered = useSelector(
    (state: ReduxState) => !!state.admin.resources['settings-clients-securefile-excessive-decrypt-config']
  );

  const savedHandlers = useDefaultSavedHandlers("Save SecureFile Settings")

  const listCtrl = useListController({ resource, basePath: '' })
  return (
    <Content pageName="Alarms and Notifications" resourceName="Alarms and Notifications" breadcrumbs={props.options?.breadcrumbs}>
      <Card sx={{ 
        mt: 5,
        '& div.MuiTabPanel-root': { px: 0 }
      }}>
        {!hasSecureFileFeature
          ? <>
              <CardHeader title="Notifications" />  
              <Divider />
              <Scrollbar>
                <ListContextProvider value={listCtrl}>
                  <NotificationSettingsBody {...props} />
                </ListContextProvider>
              </Scrollbar>
            </>
          : <TabContext value={states.currTab}>
              <Tabs
                indicatorColor="secondary"
                textColor="secondary"
                value={states.currTab}
                onChange={async (e, newVal: TabOption) => { 
                  // const pass = await (
                  //     states.currTab === 'basic'  ? tab1Guard 
                  //   : states.currTab === 'custom' ? tab2Guard 
                  //   : tab3Guard).current();
                  if (true) { 
                    setStates(v => ({...v, currTab: newVal }))
                    history.push(location.pathname+"#"+newVal)
                  }
                }}
                sx={{ '& button.MuiButtonBase-root': { height: 64, px: 4, textTransform: 'none' } }}
              >
                <Tab label="Basic Notifications" value="general" />
                <Tab label="SecureFile - Excessive Decryption Alarm" value="excessive-decrypt" />
              </Tabs>
              <Divider />
              <Scrollbar>
                <ListContextProvider value={listCtrl}>
                  <TabPanel value="general">
                    <NotificationSettingsBody {...props} />
                  </TabPanel>
                </ListContextProvider>
                <TabPanel value="excessive-decrypt">
                  {isRegistered &&
                    <Edit resource={excessiveDecryptResource} basePath="" id="edit" {...savedHandlers} mutationMode="pessimistic">
                      <ExcessiveDecryptPolicySetting />
                    </Edit>
                  }
                </TabPanel>
              </Scrollbar>
            </TabContext>
        }
      </Card>
    </Content>
  )
}


const NotificationSettingsBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const listCtx = useListContext<NotificationAssignDto & RecPatch>()
  const { data, refetch } = listCtx

  const { loaded, data: rolesData } = useGetMainList<RoleResponseDto&RecPatch>('org-roles', pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY' })
  const roles = Object.values(rolesData || {}).sort((a, b) => a.id - b.id)

  const initialValues = useMemo(() => cloneDeep(data), [data])

  const formikCtrl = useFormik({ initialValues, onSubmit: () => {} })
  const { setValues } = formikCtrl

  useEffect(() => {
    if (initialValues) {
      setValues(initialValues)
    }
  }, [initialValues, setValues])

  const rolesAssignHandlers = useDefaultSavedHandlers("Update Roles Assignment")
  rolesAssignHandlers.onSuccess.merge(refetch)

  return (
    <Box sx={{ minWidth: 1150 }}>
      <OdForm formikCtrl={formikCtrl}>
        <Datagrid 
          hasBulkActions={false} 
          sx={{ 
            '& th' : { py: 2, '& span': { display: 'inline-block' } },
            '& td' : { py: 2 },
          }}
          {...listCtx}
        >
          <FunctionField
            label={<Box component="span" sx={{ maxWidth: 230 }}>Nofitication Type</Box>}
            sortable={false}
            render={(d?:NotificationAssignDto&RecPatch) => (
              d && d.notificationType &&
              <Typography component="span" color="textPrimary" variant="body2">
                {notiTypeInfoMap[d.notificationType]?.name}
              </Typography>
            )}
          />
          <FunctionField
            source="roles"
            label="Roles Assign"
            sortable={false}
            render={(d:any) => (
              <CreateOrEdit 
                id={d.id}
                record={d} 
                basePath="" 
                resource={resource} 
                mutationMode="pessimistic" 
                {...rolesAssignHandlers}
              >
                <RolesAssign roles={roles} />
              </CreateOrEdit>
            )}
          />
          <FunctionField 
            label="Description"
            sortable={false}
            render={(d?:NotificationAssignDto&RecPatch) => (
              d && d.notificationType &&
              <Typography color="textSecodary" variant="caption" sx={{ maxWidth: 250, display: 'inline-block' }}>
                {notiTypeInfoMap[d.notificationType]?.description}
              </Typography>
            )}
          />
        </Datagrid>
      </OdForm>
      <Backdrop open={!loaded} />
    </Box>
  )
}

const notiTypeInfoMap = {
  [EmailType.GenericAlert]: { name: 'Generic Alert', description: 'General purpose notification that does not belong to a specific topic.' },
  [EmailType.BackupAlert]: { name: 'Backup Alert', description: 'Notification of Warning or Error status associated with database backup.' },
  [EmailType.BackupNotReceivedAlert]: { name: 'Backup Not Received Alert', description: 'Notification on missing database backup for more than one day.' },
  [EmailType.ValidationAlert]: { name: 'Validation Alert', description: 'Notification of Warning or Error status associated with the validation of database backups.' },
} as Record<EmailType, {name:string, description:string}>

export default NotificationSettings;

const RolesAssign: FC<{roles: RoleResponseDto[]}> = ({ roles }) => {
  const hasWritePermit = useHasPermit([ORGANIZATION_USERS])
  const ntaDto = useRecordContext<NotificationAssignDto&RecPatch>()
  const emailType = ntaDto.notificationType ?? EmailType.Unknown
  const { values, setFieldValue } = useFormikContext<Record<EmailType, NotificationAssignDto>>()
  const assignedDto = values[emailType]
  const rolesSelected = useMemo(() => {
    const s1 = new Set<number>(assignedDto?.roles ?? [])
    const s2 = new Set<number>(assignedDto?.customRoles ?? [])
    return (roles ?? []).flatMap(r => {
      const s = r.organizationId === 0 ? s1 : s2
      return s.has(r.roleId!) ? [r] : []
    })
  }, [assignedDto, roles.length])
  
  const save = useSimpleSaveAction()
  const saveRef = useRef(save)
  if (saveRef.current !== save) {
    saveRef.current = save
  }
  const saveDebounced = useMemo(() => debounce((a, b) => saveRef.current(a, b) , 1200), [])

  return (
    <Box sx={{ width: 500 }}>
      <Autocomplete
        disabled={!hasWritePermit}
        multiple
        options={roles}
        getOptionLabel={(option:RoleResponseDto) => option.name || ''}
        value={rolesSelected}
        filterSelectedOptions
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder="Choose roles"
            inputProps={{
              ...params.inputProps,
              'aria-label': ntaDto.notificationType !== undefined 
                ? notiTypeInfoMap[ntaDto.notificationType]?.name
                : '',
            }}
          />
        )}
  
        onChange={(e, value, reason) => {
          if (reason === 'selectOption' || reason === 'removeOption') {
            const assign = values[emailType]
            const roles = value.reduce((acc, r) => {
              const arr = r.organizationId === 0 ? acc.roles : acc.customRoles
              arr.push(r.roleId!)
              arr.sort()
              return acc
            }, { roles: [] as number[], customRoles: [] as number[] })
            setFieldValue(emailType+'', 
              {
                ...assign,
                ...roles,
              }
            )
            saveDebounced({ id: emailType, roles }, {
              onSuccess: () => {
                ntaDto.roles = roles.roles
                ntaDto.customRoles = roles.customRoles
              }
            })
          }
        }}
      />
    </Box>
  )
}


const buildInitState = (rolesData: Record<string, RoleResponseDto & RecPatch> | undefined, record: ExcessiveDecryptSettingDto|undefined) => {
  const orgId = !rolesData ? -1 : (Object.values(rolesData).find(r => r.organizationId !== 0)?.organizationId ?? -1)
  const roles : RoleResponseDto[] = 
    !rolesData ? [] : 
    [(record?.roles ?? []).map(x => rolesData[`0-${x}`]).filter(r => !!r),
    (record?.customRoles ?? []).map(x => rolesData[`${orgId}-${x}`]).filter(r => !!r)].reduce((acc, x) => [...acc, ...x])
  return {
    settings: {
      isEnabled: record?.isEnabled ?? false,
      timeRange: record?.timeRange ?? '',
      threshold: record?.threshold ?? '',
      roles,
      users: (record?.users ?? []) as Array<{username?:string|null, userId?:number, id:any}>,
    },
    touched: null as any,
    errors: null as any,
    userQuery: {
      text:'',
      timer: null as number|null,
      filters: { name: '' }
    },
    showGuardAlert: false,
  }
}

const ExcessiveDecryptPolicySetting: FC = () => {
  const record : ExcessiveDecryptSettingDto|undefined = useRecordContext() as any
  const { data: rolesData } = useGetMainList<RoleResponseDto&RecPatch>('org-roles', pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY' })
  const roles = Object.values(rolesData || {}).sort((a, b) => a.organizationId! !== b.organizationId! ? a.organizationId! - b.organizationId! : (a.name! < b.name! ? -1 : 1))

  const [states, setStates] = useState(buildInitState(rolesData, record));
  
  useEffect(() => {
    setStates(buildInitState(rolesData, record))
  }, [rolesData, record])
  
  const { data: usersData, ids, loading } = useGetMainList<UserResponseDto&RecPatch>('settings/organization/user-manage', pages(), sorts(), states.userQuery.filters, { action: 'CUSTOM_DATA_QUERY' })
  const users = usersData && ids ? ids.map(id => usersData[id]).sort((a, b) => a.username! < b.username! ? -1 : 1) : []
  
  const validate = (x: typeof states) => {
    const e_ls = []
    if (x.settings.roles.length === 0 && x.settings.users.length === 0) {
      e_ls.push({ emailTarget: 'Should at least specify one role or user' })
    }
    if (x.settings.threshold == null || x.settings.threshold === '') {
      e_ls.push({ threshold: 'Threshold is required' })
    }
    if (x.settings.timeRange == null || x.settings.timeRange === '') {
      e_ls.push({ timeRange: 'Time Range is required' })
    }
    return e_ls.length === 0 ? null : e_ls.reduce((acc, x) => ({...acc, ...x}), {} as any)
  }

  // tabGuard.current = () => new Promise<boolean>((resolve) => {
  //   let pass = Object.keys(states.touched ?? {}).length === 0
  //   resolve(pass)
  //   if (!pass || states.showGuardAlert) {
  //     setStates(v => ({...v, showGuardAlert: !pass}))
  //   }
  // })
  return (
    <Box sx={{ px: 2.5 }}>
      {states.showGuardAlert &&
        <FormHelperText sx={{ color: theme => theme.palette.warning.main, mb: 1.5 }}>There are unsaved changes, please make sure you have saved your edits.</FormHelperText>}
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <FormGroup sx={{ mb: 1 }}>
            <FormControlLabel 
              label="Enable Excessive Decryption Alarm" 
              control={
                <Switch checked={states.settings.isEnabled} 
                  onChange={(e, checked) => setStates(v => ({...v, settings: {...v.settings, isEnabled: checked},
                    touched: {...v.touched, isEnabled: true },
                  }))} />
              } 
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{
            '& p.MuiFormHelperText-root': { whiteSpace: 'nowrap' }
          }}>
            <Typography variant="subtitle2"
              sx={{ borderLeft: theme => `${theme.palette.secondary.light} 4px solid`, pl: 1 }}
            >Rule</Typography>
            <Box sx={{ ml: 2 }}>
              <Stack direction="row" alignItems="baseline" >
                Allow maximun 
                <MuiTextField label="Threshold" sx={{ mx: 1.5, width: 96 }} type="number" variant="standard" InputLabelProps={{ shrink: true, }} 
                  value={states.settings.threshold}
                  onChange={(e) => {
                    let i : number|'' = Number.parseInt(e.target.value)
                    i = isNaN(i) ? '' : i < 1 ? 1 : i > 32767 ? 32767 : i
                    setStates(v => ({...v, settings: {...v.settings, threshold: i }, 
                      touched: {...v.touched, threshold: true },
                      errors: 
                          i === '' ? {...v.errors, threshold: 'Threshold is required' } 
                        : v.errors?.threshold ? (delete v.errors.threshold, v.errors) : v.errors
                    }))
                  }}
                  error={Boolean(states.touched?.threshold && states.errors?.threshold)}
                  helperText={states.errors?.threshold}
                /> 
                file decryptions 
              </Stack>
              <Stack direction="row" alignItems="baseline" sx={{ mt: 1.5, pl: 8 }} >
                within 
                <MuiTextField label="Time Range" sx={{ mx: 1.5, width: 96 }} type="number" variant="standard" InputLabelProps={{ shrink: true, }} 
                  value={states.settings.timeRange}
                  onChange={(e) => {
                    let i : number|'' = Number.parseInt(e.target.value)
                    i = isNaN(i) ? '' : i < 1 ? 1 : i > 32767 ? 32767 : i
                    setStates(v => ({...v, settings: {...v.settings, timeRange: i }, 
                      touched: {...v.touched, timeRange: true },
                      errors: 
                          i === '' ? {...v.errors, timeRange: 'Time Range is required' } 
                        : v.errors?.timeRange ? (delete v.errors.timeRange, v.errors) : v.errors
                    }))
                  }}
                  error={Boolean(states.touched?.timeRange && states.errors?.timeRange)}
                  helperText={states.errors?.timeRange ?? formatTimeRangeDisplay(states.settings.timeRange as any)}
                /> 
                hours for every user.
              </Stack>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box>
            <Typography variant="subtitle2"
              sx={{ borderLeft: theme => `${theme.palette.secondary.light} 4px solid`, pl: 1, mb: 2 }}
            >Notification</Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" component="p" sx={{ mb: 2 }}>
                In case of rule violation, notification emails will be sent to:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ width: 850 }}>
                    <Autocomplete
                      multiple
                      options={roles}
                      getOptionLabel={(option:RoleResponseDto) => option.name || ''}
                      value={states.settings.roles}
                      filterSelectedOptions
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          placeholder="Choose roles"
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': '',
                          }}
                        />
                      )}
                
                      onChange={(e, value, reason) => {
                        if (reason === 'selectOption' || reason === 'removeOption') {
                          setStates(v => ({...v, settings: {...v.settings, roles: value }, touched: {...v.touched, roles: true }}))
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ width: 850 }}>
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={(option:UserResponseDto) => option.username!}
                      isOptionEqualToValue={(option, value) => option.username === value.username}
                      value={states.settings.users}
                      filterSelectedOptions
                      loading={loading}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          placeholder="Choose users"
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': '',
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                
                      onChange={(e, value, reason) => {
                        if (reason === 'selectOption' || reason === 'removeOption') {
                          setStates(v => ({...v, settings: {...v.settings, users: value as any }, touched: {...v.touched, users: true }}))
                        }
                      }}
                      inputValue={states.userQuery.text}
                      onInputChange={(e, value, reason) => {
                        setStates(v => {
                          var v1 = {...v}
                          v1.userQuery.text = value
                          if (v.userQuery.timer) { window.clearTimeout(v.userQuery.timer) }
                          v1.userQuery.timer = window.setTimeout(() => { 
                            setStates(v1 => {
                              const v2 = {...v1}
                              v2.userQuery.timer = null
                              v2.userQuery.filters = {name: value} 
                              return v2
                            })
                          }, 300)
                          return v1
                        })
                      }}
                    />
                  </Box>
                </Grid>
                {states.errors?.emailTarget &&
                  <Grid item xs={12}>
                    <FormHelperText error >{states.errors?.emailTarget}</FormHelperText>
                  </Grid>}
              </Grid>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
        </Grid>
      </Grid>
      {states.errors?.submit && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>
            {states.errors?.submit}
          </FormHelperText>
        </Box>
      )}
      <Stack direction="row" sx={{ py: 2 }} >
        {/* <Button
          color="primary"
          variant="outlined"
          onClick={onClose}
        >
          Close
        </Button> */}
        <Button sx={{ mr: 2 }}
          color="primary"
          variant="outlined"
          onClick={() => setStates(buildInitState(rolesData, record)) }
        >
          Discard
        </Button>
        <WithSave 
          render={({ save, saving }) =>
            <Button
              color="primary"
              disabled={!states.touched || saving}
              type="submit"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                setStates(v => {
                  const v1 = {...v,
                    touched: {timeRange:true, threshold:true, emailTarget:true},
                    showGuardAlert: false,
                  }
                  v1.errors = validate(v1)
                  if (!v1.errors) {  
                    setTimeout(() => {
                      const payload: ExcessiveDecryptSettingInput = {
                        isEnabled: v1.settings.isEnabled,
                        timeRange: v1.settings.timeRange as any,
                        threshold: v1.settings.threshold as any,
                        roles: v1.settings.roles.filter(r => r.organizationId === 0).map(r => r.roleId!),
                        customRoles: v1.settings.roles.filter(r => r.organizationId !== 0).map(r => r.roleId!),
                        users: v1.settings.users.map(u => u.userId!),
                      }
                      save(payload, {
                        onSuccess: () => {
                          setStates(v2 => ({...v2, touched: null}))
                        },
                        onFailure: (err:any) => {
                          setStates(v2 => ({...v2, errors: { submit: err.message }}))
                        },
                      })
                    })
                  }
                  return v1
                })
                return false
              }}
            >
              Save
            </Button>
          }
        />
      </Stack>
    </Box>
  )
}

const formatTimeRangeDisplay = (i?: number|null) => {
  if (!i) { return null }
  const d = Math.floor(i / 24)
  const h = i % 24
  return [d ? `${d} day${d > 1 ? 's' : ''}` : '', h ? `${h} hour${h > 1 ? 's' : ''}` : ''].join(' ')
}


